import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../store/useAuthStore'

/** Detect Supabase password-recovery redirect in both implicit (hash) and PKCE (search) flows. */
function isRecoveryFlow(): boolean {
  const hash = new URLSearchParams(window.location.hash.slice(1))
  const search = new URLSearchParams(window.location.search)
  return hash.get('type') === 'recovery' || search.get('type') === 'recovery'
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--tco-surface)',
  border: '1px solid var(--tco-line)',
  borderRadius: 'var(--tco-radius)',
  padding: '0.625rem 0.75rem',
  color: 'var(--tco-ink)',
  fontSize: '15px',
  fontFamily: 'var(--tco-font-sans)',
  fontWeight: 400,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11px',
  fontFamily: 'var(--tco-font-sans)',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--tco-ink-3)',
  marginBottom: '0.5rem',
}

const errorStyle: React.CSSProperties = {
  marginBottom: '1rem',
  padding: '0.625rem 0.75rem',
  background: 'var(--tco-neg-soft)',
  border: '1px solid var(--tco-neg)',
  borderRadius: 'var(--tco-radius)',
  color: 'var(--tco-neg)',
  fontSize: '13px',
  fontFamily: 'var(--tco-font-sans)',
}

const submitBtnStyle = (disabled: boolean): React.CSSProperties => ({
  width: '100%',
  background: disabled ? 'var(--tco-line-soft)' : 'var(--tco-ink)',
  border: `1px solid ${disabled ? 'var(--tco-line)' : 'var(--tco-ink)'}`,
  color: disabled ? 'var(--tco-ink-3)' : 'var(--tco-bg)',
  borderRadius: 'var(--tco-radius)',
  padding: '0.625rem',
  fontSize: '14px',
  fontFamily: 'var(--tco-font-sans)',
  fontWeight: 550,
  letterSpacing: '0.04em',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'background 0.15s',
})

export default function AuthCallbackPage() {
  const navigate = useNavigate()
  const { setSession } = useAuthStore()

  const [mode, setMode] = useState<'loading' | 'recovery'>('loading')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Phase 25: opt the body into the ledger theme on /auth/callback as well, so
  // font stack + tabular numerals + paper background are consistent with the
  // rest of the app. Layout.tsx applies the same class on protected routes;
  // LoginPage applies it on /login.
  useEffect(() => {
    document.body.classList.add('tco-ledger')
    return () => {
      document.body.classList.remove('tco-ledger')
    }
  }, [])

  useEffect(() => {
    let recoveryHandled = false

    // Primary: onAuthStateChange fires PASSWORD_RECOVERY when Supabase exchanges a
    // recovery token in PKCE flow — at that point type=recovery is gone from the URL.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoveryHandled = true
        if (session) setSession(session)
        setMode('recovery')
      }
    })

    // Fallback: URL-param check (implicit flow) + redirect logic for non-recovery sessions.
    // Phase 25.6: any pending mode pick from /welcome is applied by
    // useAppModeStore.initialize once the auth store flips, so we don't need
    // to PATCH here.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (recoveryHandled) return
      if (!session) {
        navigate('/login', { replace: true })
        return
      }
      setSession(session)
      if (isRecoveryFlow()) {
        setMode('recovery')
      } else {
        navigate('/', { replace: true })
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function handleResetSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    setSubmitting(true)
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword })
    setSubmitting(false)
    if (updateError) {
      setError(updateError.message)
    } else {
      navigate('/', { replace: true })
    }
  }

  if (mode === 'loading') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--tco-bg)',
          color: 'var(--tco-ink-3)',
          fontFamily: 'var(--tco-font-sans)',
          fontSize: '14px',
          fontWeight: 400,
          letterSpacing: '0.02em',
        }}
      >
        Signing you in…
      </div>
    )
  }

  // Recovery mode — show password reset form
  const disabled = submitting || !newPassword || !confirmPassword
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--tco-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        fontFamily: 'var(--tco-font-sans)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          background: 'var(--tco-surface)',
          border: '1px solid var(--tco-line)',
          borderLeft: '3px solid var(--tco-ink)',
          borderRadius: 'var(--tco-radius)',
          padding: '2rem',
          position: 'relative',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div
            style={{
              fontSize: '1.6rem',
              fontFamily: 'var(--tco-font-sans)',
              fontWeight: 700,
              color: 'var(--tco-ink)',
              letterSpacing: '-0.01em',
              marginBottom: '0.25rem',
            }}
          >
            VehicleTCO
          </div>
          <div style={{ fontSize: '14px', color: 'var(--tco-ink-2)', fontFamily: 'var(--tco-font-sans)', fontWeight: 400 }}>
            Set a new password
          </div>
        </div>

        <form onSubmit={handleResetSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="new-password" style={labelStyle}>New password</label>
            <input
              id="new-password"
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              style={inputStyle}
              onFocus={(e) => { e.target.style.borderColor = 'var(--tco-ink)'; e.target.style.boxShadow = '0 0 0 2px rgba(23,24,26,0.08)' }}
              onBlur={(e) => { e.target.style.borderColor = 'var(--tco-line)'; e.target.style.boxShadow = 'none' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="confirm-password" style={labelStyle}>Confirm password</label>
            <input
              id="confirm-password"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
              style={inputStyle}
              onFocus={(e) => { e.target.style.borderColor = 'var(--tco-ink)'; e.target.style.boxShadow = '0 0 0 2px rgba(23,24,26,0.08)' }}
              onBlur={(e) => { e.target.style.borderColor = 'var(--tco-line)'; e.target.style.boxShadow = 'none' }}
            />
          </div>

          {error && <div style={errorStyle}>{error}</div>}

          <button
            type="submit"
            disabled={disabled}
            style={submitBtnStyle(disabled)}
          >
            {submitting ? 'Updating…' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
